import * as m from '@staiter/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import type { JSX } from 'react';
import { PageHeader } from '~/components/data';
import { Button } from '~/components/primitives';
import { type AdminJobName, fetchScheduledJobs, triggerJobNow } from '~/lib/api/admin-jobs';
import styles from './_auth.admin.jobs.module.css';

/**
 * S-FIX-1-FE.D.2 — Admin Jobs page (BullMQ scheduler diagnostics).
 *
 * Показывает 3 repeatable jobs: NBKZ fetch / FX revaluation / Bank-import cleanup.
 * Admin может triggernuть вручную через "Run now".
 */
export const Route = createFileRoute('/_auth/admin/jobs')({ component: AdminJobsPage });

function jobLabel(name: AdminJobName): string {
  if (name === 'nbkz_fetch') return m.admin_jobs_name_nbkz_fetch();
  if (name === 'fx_revaluation') return m.admin_jobs_name_fx_revaluation();
  return m.admin_jobs_name_bank_import_cleanup();
}

function statusLabel(status: 'ok' | 'error' | 'skipped' | 'never_run'): string {
  if (status === 'ok') return m.admin_jobs_status_ok();
  if (status === 'error') return m.admin_jobs_status_error();
  if (status === 'skipped') return m.admin_jobs_status_skipped();
  return m.admin_jobs_status_never_run();
}

function AdminJobsPage(): JSX.Element {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['admin', 'jobs'],
    queryFn: fetchScheduledJobs,
    staleTime: 30_000,
  });
  const triggerMut = useMutation({
    mutationFn: (name: AdminJobName) => triggerJobNow(name),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'jobs'] });
    },
  });

  if (!query.data) {
    return (
      <div className={styles.pageNarrow}>
        <PageHeader title={m.route_admin_jobs_title()} />
      </div>
    );
  }

  if (!query.data.scheduler_available) {
    return (
      <div className={styles.pageNarrow}>
        <PageHeader title={m.route_admin_jobs_title()} />
        <div className={styles.warningBox} role="alert">
          {m.admin_jobs_scheduler_unavailable()}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader title={m.route_admin_jobs_title()} />
      <table className={styles.table}>
        <thead>
          <tr className={styles.tableHeaderRow}>
            <th scope="col" className={styles.cell}>
              {m.admin_jobs_col_name()}
            </th>
            <th scope="col" className={styles.cell}>
              {m.admin_jobs_col_cron()}
            </th>
            <th scope="col" className={styles.cell}>
              {m.admin_jobs_col_next_run()}
            </th>
            <th scope="col" className={styles.cell}>
              {m.admin_jobs_col_last_run()}
            </th>
            <th scope="col" className={styles.cell}>
              {m.admin_jobs_col_last_status()}
            </th>
            <th scope="col" className={styles.cell} aria-label="actions" />
          </tr>
        </thead>
        <tbody>
          {query.data.jobs.map((j) => (
            <tr key={j.name} className={styles.row}>
              <td className={styles.cell}>
                <strong>{jobLabel(j.name)}</strong>
                <div className={styles.metaLine}>{m.admin_jobs_provenance()}</div>
              </td>
              <td className={styles.cron}>
                {j.cron_pattern} <span className={styles.cronTz}>({j.tz})</span>
              </td>
              <td className={styles.numCell}>{j.next_run ?? '—'}</td>
              <td className={styles.numCell}>{j.last_run ?? '—'}</td>
              <td className={styles.cell}>
                <span
                  className={
                    j.last_status === 'ok'
                      ? styles.statusOk
                      : j.last_status === 'error'
                        ? styles.statusError
                        : styles.statusMuted
                  }
                >
                  {statusLabel(j.last_status)}
                </span>
                {j.last_status === 'error' && j.last_error_code && (
                  <div className={styles.metaLine}>{j.last_error_code}</div>
                )}
              </td>
              <td className={styles.cell}>
                <Button
                  variant="ghost"
                  onClick={() => triggerMut.mutate(j.name)}
                  loading={triggerMut.isPending && triggerMut.variables === j.name}
                >
                  {m.admin_jobs_run_now()}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
